import { strFromU8, unzipSync } from 'fflate';
import GeoJSON from 'ol/format/GeoJSON';
import type Feature from 'ol/Feature';

export interface CanvasImportedLayer {
  name: string;
  features: Feature[];
}

export function isCanvasImportFileName(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext === 'json' || ext === 'geojson' || ext === 'zip';
}

export function layerNameFromImportPath(path: string): string {
  const base = path.split(/[/\\]/).pop() ?? 'layer';
  return base.replace(/\.(geojson|json)$/i, '').trim() || 'layer';
}

export function readGeoJsonFeatures(
  data: object,
  featureProjection: string,
): Feature[] {
  const format = new GeoJSON();
  return format.readFeatures(data, {
    featureProjection,
    dataProjection: 'EPSG:4326',
  });
}

export function parseCanvasGeoJsonText(
  text: string,
  featureProjection: string,
  layerName: string,
): CanvasImportedLayer | null {
  const data = JSON.parse(text.replace(/^\uFEFF/, '')) as object;
  const features = readGeoJsonFeatures(data, featureProjection);
  if (!features.length) return null;
  return { name: layerName, features };
}

function isZipJunkPath(path: string): boolean {
  const normalized = path.replace(/\\/g, '/');
  if (normalized.endsWith('/')) return true;
  if (normalized.startsWith('__MACOSX/') || normalized.includes('/__MACOSX/')) {
    return true;
  }
  const file = normalized.split('/').pop() ?? '';
  return file.startsWith('.') || file === 'Thumbs.db';
}

function isGeoJsonPath(path: string): boolean {
  const lower = path.toLowerCase();
  return lower.endsWith('.geojson') || lower.endsWith('.json');
}

/** Parse unzipped Canvas export entries (one GeoJSON file per layers-panel row). */
export function parseCanvasImportZipEntries(
  files: Record<string, Uint8Array>,
  featureProjection: string,
): CanvasImportedLayer[] {
  const layers: CanvasImportedLayer[] = [];

  for (const path of Object.keys(files).sort((a, b) => a.localeCompare(b))) {
    if (isZipJunkPath(path) || !isGeoJsonPath(path)) continue;
    const raw = files[path];
    if (!raw?.length) continue;
    try {
      const layer = parseCanvasGeoJsonText(
        strFromU8(raw),
        featureProjection,
        layerNameFromImportPath(path),
      );
      if (layer) layers.push(layer);
    } catch {
      // Skip unreadable entries; the caller reports if nothing valid remains.
    }
  }

  return layers;
}

/** Unzip a Canvas GeoJSON export (one `.geojson` file per layers-panel row). */
export function parseCanvasImportZip(
  bytes: Uint8Array,
  featureProjection: string,
): CanvasImportedLayer[] {
  const copy = new Uint8Array(bytes);
  const files = unzipSync(copy);
  return parseCanvasImportZipEntries(files, featureProjection);
}
